import { Component, effect, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { MENU_ITEMS } from '@shared/models/menu-items.model';
import { NavItemComponent } from '../nav-item/nav-item.component';
import { AuthService } from '@core/services/auth.service';
import { MenuService } from '@shared/services/menu.service';
import { ThemeSideMenuSelectorComponent } from '../theme-side-menu-selector/theme-side-menu-selector.component';
import { SessionStorageService } from '@shared/services/session-storage.service';
import { ICurrency } from '@shared/models/currency.model';
import { CurrencyMenuSelectorComponent } from '../currency-menu-selector/currency-menu-selector.component';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { MenuItem } from '@shared/models/menu.model';
import { LoadingComponent } from '../loading/loading.component';

@Component({
  selector: 'app-nav',
  imports: [MatListModule, NavItemComponent, ThemeSideMenuSelectorComponent, CurrencyMenuSelectorComponent, LoadingComponent],
  templateUrl: './nav.component.html',
  styleUrl: './nav.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class NavComponent implements OnInit, OnDestroy {
  menuItems: MenuItem[] = [];
  private _authService = inject(AuthService);
  private _menuService = inject(MenuService);
  private _sessionStorageService = inject(SessionStorageService);
  layoutContent: 'menu' | 'theme' | 'currency' = 'menu';
  selectedTheme: 'light' | 'dark' = 'light';
  selectedCurrency: ICurrency | null = null;
  isMobileOrTablet: boolean = false;
  private readonly mobileQuery = 'screen and (max-width: 599px)';
  private readonly tabletQuery = 'screen and (min-width: 600px) and (max-width: 1023px)';
  breakpointObserver = inject(BreakpointObserver);
  backToMenuSelected = false;
  menuChecked = false;

  userConfigSign = effect(() => {
    if (this._sessionStorageService.userConfigChanged()) {
      let currency = this._sessionStorageService.config.currency;
      if (currency) this.selectedCurrency = currency;
    }
  });

  async filterMenuItems(menuItems: MenuItem[]): Promise<MenuItem[]> {
    const filteredItems = await Promise.all(
      menuItems.map(async (item) => {
        const filteredChildren = await this.filterMenuItems(item.children || []);
        const hasPermission = item.permissionNames ? (item.type === 'page' ? await this._authService.checkPermission(item.permissionNames) : true) : true;
        const isCategoryWithNoChildren = item.type === 'category' && filteredChildren.length === 0;

        if ((hasPermission && !isCategoryWithNoChildren) || (item.type === 'page' && hasPermission)) {
          return {
            ...item,
            children: filteredChildren,
          };
        }

        return null;
      }),
    );
    return filteredItems.filter((item) => item !== null) as MenuItem[];
  }

  ngOnInit() {
    this.init();
    this.breakpointObserver.observe([this.mobileQuery, this.tabletQuery]).subscribe((state: BreakpointState) => {
      this.isMobileOrTablet = state.breakpoints[this.mobileQuery] || state.breakpoints[this.tabletQuery];
      this.getTheme();
    });
    this.getTheme();

    let currency = this._sessionStorageService.config.currency;
    if (currency) {
      this.selectedCurrency = currency;
    }
  }

  ngOnDestroy(): void {
    this.userConfigSign && this.userConfigSign.destroy();
  }

  getTheme() {
    const html = document.getElementsByTagName('body')[0];
    let config = this._sessionStorageService.config;
    this.selectedTheme = config.theme ?? (html.getAttribute('data-theme') as 'light' | 'dark') ?? 'light';
  }

  logout() {
    this._authService.logout();
  }

  closeMenu() {
    this._menuService.toggleMenu();
    this.layoutContent = 'menu';
  }

  modeSwitch() {
    this.layoutContent = 'theme';
  }

  backToMenu() {
    this.backToMenuSelected = true;
    this.layoutContent = 'menu';

    setTimeout(() => {
      this.backToMenuSelected = false;
    }, 400);
  }

  changeCurrency() {
    this.layoutContent = 'currency';
  }

  setNewCurrency(currency: ICurrency) {
    this.selectedCurrency = currency;
    let config = this._sessionStorageService.config;
    currency.symbol = currency.symbol ?? '';
    config.currency = currency;
    this._sessionStorageService.setConfig(config);
    this._sessionStorageService.userConfigChanged.set(false);
    this._sessionStorageService.userConfigChanged.set(true);
  }

  toggleTheme(theme: any) {
    const html = document.getElementsByTagName('body')[0];
    const currentTheme = html.getAttribute('data-theme');
    const newTheme = currentTheme === theme ? 'light' : theme;
    this.selectedTheme = newTheme as 'light' | 'dark';
    let config = this._sessionStorageService.config;
    config.theme = newTheme;
    this._sessionStorageService.setConfig(config);
    this._sessionStorageService.userConfigChanged.set(false);
    this._sessionStorageService.userConfigChanged.set(true);
    html.setAttribute('data-theme', newTheme);
  }

  private async init() {
    if (this._authService.isPermissionsGet) {
      this.menuItems = await this.filterMenuItems(MENU_ITEMS);
      this.menuChecked = true;
      return;
    }
    setTimeout(() => {
      this.init();
    }, 1000);
  }
}
